import { useCallback, useState } from 'react'
import { DataTable } from '@/components/ui/DataTable'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { containerLogsColumns } from '@/pages/Platform/PlatformContainerLogsTable/platformContainerLogsTableColumns'
import { Button } from '@/components/shadcn/button'
import { SpinnerIosRegular } from '@fluentui/react-icons'
import { ApplicationsBreadcrumb } from '../ApplicationsBreadcrumbs'
import { useCurrentApplication } from '../useCurrentApplication'
import {
  defaultLogTimeFilters,
  LogTimeFilterPopover,
  LogTimeFilters,
} from '@/pages/shared-components/ContainerLogs/containerLogFilters'

type ContainerLogResult = RestSchemas['Services.Support.ContainerLogResult']

export function AppContainerLogsPage() {
  const { applicationId, appName } = useCurrentApplication()
  const { getSupportApiQueryApplicationContainerLogs } = useRestClient()

  const [isRefreshingLogs, setIsRefreshingLogs] = useState(false)
  const [logTimeFilters, setLogTimeFilters] = useState<LogTimeFilters>(defaultLogTimeFilters)

  const { data: logs = [], isFetching: logsPending } = getSupportApiQueryApplicationContainerLogs.useQuery({
    query: { applicationId, ...logTimeFilters },
  })

  const onRefreshLogs = useCallback(async () => {
    const refreshedEndTime = new Date().toISOString()
    const updatedFilters = { ...logTimeFilters, endTime: refreshedEndTime }
    setLogTimeFilters(updatedFilters)
    try {
      setIsRefreshingLogs(true)
      await getSupportApiQueryApplicationContainerLogs.invalidateQueries({
        parameters: { query: { applicationId, ...updatedFilters } },
      })
    } finally {
      setTimeout(() => setIsRefreshingLogs(false), 300)
    }
  }, [applicationId, getSupportApiQueryApplicationContainerLogs, logTimeFilters])

  return (
    <>
      <ApplicationsBreadcrumb title="Container Logs" />
      <TablePageContainer fullHeight>
        <div className="px-5">
          {logsPending && (
            <div className="flex items-center gap-2 px-1 py-2 text-muted-foreground">
              <SpinnerIosRegular className="animate-spin" />
              <span>Loading logs…</span>
            </div>
          )}
          <DataTable<ContainerLogResult, unknown>
            title={`Container Logs - ${appName}`}
            action={
              <div className="flex items-center gap-2">
                <LogTimeFilterPopover filters={logTimeFilters} onChange={setLogTimeFilters} />
                <Button size="sm" variant="outline" onClick={onRefreshLogs} disabled={isRefreshingLogs}>
                  {isRefreshingLogs ? (
                    <>
                      <SpinnerIosRegular className="animate-spin" />
                      Refreshing…
                    </>
                  ) : (
                    'Refresh'
                  )}
                </Button>
              </div>
            }
            columns={containerLogsColumns}
            data={(logs as ContainerLogResult[]) || []}
          />
        </div>
      </TablePageContainer>
    </>
  )
}
